import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Layout } from '@/components/layout/Layout';
import { Card } from '@/components/ui/card';
import { Slider } from '@/components/ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Building2, Calculator, CreditCard, Home, Landmark, ListChecks, Percent } from 'lucide-react';
import { Button } from '@/components/ui/button';

const loanProducts = [
  { id: 'standard', name: 'Standard Home Loan', rate: 14.5 },
  { id: 'diaspora', name: 'Diaspora Mortgage', rate: 12.0 },
  { id: 'affordable', name: 'Affordable Housing Loan', rate: 10.5 },
  { id: 'construction', name: 'Construction Loan', rate: 15.0 },
];

function formatETB(amount: number) {
  return new Intl.NumberFormat('en-ET').format(Math.round(amount));
}

export default function MortgageCalculator() {
  const { t } = useTranslation();
  const [productId, setProductId] = useState('standard');
  const [propertyPrice, setPropertyPrice] = useState(5_000_000);
  const [downPaymentPct, setDownPaymentPct] = useState(30);
  const [loanYears, setLoanYears] = useState(20);

  const product = loanProducts.find(p => p.id === productId)!;

  const calc = useMemo(() => {
    const downPayment = propertyPrice * (downPaymentPct / 100);
    const loanAmount = propertyPrice - downPayment;
    const monthlyRate = product.rate / 100 / 12;
    const totalMonths = loanYears * 12;
    const monthly =
      monthlyRate > 0
        ? (loanAmount * monthlyRate * Math.pow(1 + monthlyRate, totalMonths)) /
          (Math.pow(1 + monthlyRate, totalMonths) - 1)
        : loanAmount / totalMonths;
    return { downPayment, loanAmount, monthly, totalMonths };
  }, [propertyPrice, downPaymentPct, loanYears, product.rate]);

  return (
    <Layout>
      <div className="bg-background min-h-screen py-8">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-2xl md:text-3xl font-bold text-foreground">
              Mortgage Calculator
            </h1>
            <span className="hidden sm:inline text-sm text-muted-foreground">
              ETB {formatETB(calc.monthly)} Installment | {formatETB(calc.loanAmount)} Loan
            </span>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            {/* Left — Inputs */}
            <Card className="lg:col-span-3 p-6 space-y-8">
              <div className="flex items-center gap-3 mb-2">
                <Landmark className="h-8 w-8 text-primary" />
                <span className="font-semibold text-lg text-foreground">Loan Parameters</span>
              </div>

              {/* Product Type */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">Product Type</label>
                <Select value={productId} onValueChange={setProductId}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {loanProducts.map(p => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name} — {p.rate}% p.a.
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Property Price */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-foreground">Property Price</label>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="font-semibold text-foreground">ETB {formatETB(propertyPrice)}</span>
                  <span className="text-muted-foreground">
                    {(propertyPrice / 1_000_000).toFixed(2)} Million
                  </span>
                </div>
                <Slider
                  value={[propertyPrice]}
                  onValueChange={v => setPropertyPrice(v[0])}
                  min={500_000}
                  max={100_000_000}
                  step={100_000}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>500K</span>
                  <span>100M</span>
                </div>
              </div>

              {/* Down Payment & Loan Period side by side */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-3">
                  <label className="text-sm font-medium text-foreground">Down Payment</label>
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-semibold text-foreground">% {downPaymentPct}</span>
                    <span className="text-muted-foreground">ETB {formatETB(calc.downPayment)}</span>
                  </div>
                  <Slider
                    value={[downPaymentPct]}
                    onValueChange={v => setDownPaymentPct(v[0])}
                    min={10}
                    max={80}
                    step={1}
                    className="w-full"
                  />
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>10%</span>
                    <span>80%</span>
                  </div>
                </div>

                <div className="space-y-3">
                  <label className="text-sm font-medium text-foreground">Loan Period</label>
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-semibold text-foreground">Years {loanYears}</span>
                    <span className="text-muted-foreground">{loanYears * 12} Months</span>
                  </div>
                  <Slider
                    value={[loanYears]}
                    onValueChange={v => setLoanYears(v[0])}
                    min={1}
                    max={30}
                    step={1}
                    className="w-full"
                  />
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>1 yr</span>
                    <span>30 yrs</span>
                  </div>
                </div>
              </div>
            </Card>

            {/* Right — Results */}
            <Card className="lg:col-span-2 p-6 flex flex-col justify-between">
              <div>
                <h2 className="text-xl font-bold text-foreground mb-6">Loan Calculations</h2>

                <div className="space-y-6">
                  <ResultRow
                    icon={<Calculator className="h-5 w-5 text-primary" />}
                    label="Monthly Installments"
                    value={`ETB ${formatETB(calc.monthly)}`}
                    highlight
                  />
                  <div className="grid grid-cols-2 gap-4">
                    <ResultRow
                      icon={<Home className="h-5 w-5 text-primary" />}
                      label="Property Value"
                      value={`ETB ${formatETB(propertyPrice)}`}
                    />
                    <ResultRow
                      icon={<CreditCard className="h-5 w-5 text-primary" />}
                      label="Down Payment"
                      value={`ETB ${formatETB(calc.downPayment)}`}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <ResultRow
                      icon={<Building2 className="h-5 w-5 text-primary" />}
                      label="Loan Amount"
                      value={`ETB ${formatETB(calc.loanAmount)}`}
                    />
                    <ResultRow
                      icon={<Percent className="h-5 w-5 text-primary" />}
                      label="Interest Rate"
                      value={`${product.rate}% p.a.`}
                    />
                  </div>
                  <ResultRow
                    icon={<ListChecks className="h-5 w-5 text-primary" />}
                    label="Loan Type"
                    value={product.name}
                  />
                </div>
              </div>

              <Button className="w-full mt-8 h-12 text-base font-semibold">
                Apply For Loan
              </Button>
            </Card>
          </div>
        </div>
      </div>
    </Layout>
  );
}

function ResultRow({
  icon,
  label,
  value,
  highlight,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  highlight?: boolean;
}) {
  return (
    <div className="flex items-center gap-3">
      <div className="flex-shrink-0 h-10 w-10 rounded-full bg-muted flex items-center justify-center">
        {icon}
      </div>
      <div>
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className={`font-semibold ${highlight ? 'text-primary text-lg' : 'text-foreground text-sm'}`}>
          {value}
        </p>
      </div>
    </div>
  );
}
